import useLazyQuery from "@utils/useLazyQuery";
import {KEYS} from "@/app/constants/keys";
import {useQueryClient} from "react-query";
import useCustomMutation from "@utils/useCustomMutation";
import {
    MemberAddShopPostApi,
    MemberChangePasswordPutApi,
    MemberResetPasswordPutApi,
    MemberDeleteApi,
    MemberDetailGetApi,
    MemberInviteDeleteApi,
    MemberInviteInfoGetApi,
    MemberInviteListGetApi,
    MemberInvitePostApi,
    MemberListGetApi,
    MemberMeGetApi,
    MemberPutApi,
    MemberShopInviteInfoGetApi,
    MemberSignUpPostApi,
    MemberTempPasswordPutApi,
    RoleListGetApi,
    RolePostApi,
    MemberCertifyAccessPutApi
} from "@services/dataSource/memberApi";
import {
    MemberDeletePayload,
    MemberGetPayload,
    MemberInfoPayload,
    MemberInviteInfoEntity,
    MemberInviteInfoPayload,
    MemberInviteListPayload,
    MemberListGetPayload,
    MemberMeEntity,
    MemberMePayload,
    MemberPasswordPutPayload,
    MemberResetPasswordPutPayload,
    MemberPayload,
    MemberPutPayload,
    MemberStringPutPayload,
    RoleListGetPayload,
    RolePayload,
    MemberAccessPutPayload
} from "@services/entities/memberEntity";
import {IdEntity} from "@services/entities/commonEntity";
import {useRecoilState} from "recoil";
import {brandState, memberState, shopState, spaceState} from "@services/stores/storeState";
import {ResponseEntity} from "@services/entities/baseEntity";
import {_OPERATION_TYPE, _SERVER_ROLE_TYPE, _SYSTEM_TYPE} from "@app/constants/types";
import {AxiosError} from "axios";

// TODO :: 기능 추가 및 수정 해야함.
const MemberRepository = () => {
    const queryClient = useQueryClient();
    const [spaceInfoState, setSpaceInfoState] = useRecoilState(spaceState);
    const [shopInfoState, setShopInfoState] = useRecoilState(shopState);
    const [_, setBrandState] = useRecoilState(brandState);
    const [memberMeInfoState, setMemberMeInfoState] = useRecoilState(memberState);

    // member me fetch
    const [fetchMemberMeData, memberMeData] = useLazyQuery<MemberMePayload>(KEYS.QUERY.member.me, MemberMeGetApi);

    // member list fetch
    const [fetchMemberListData, memberListData] = useLazyQuery<MemberListGetPayload>(KEYS.QUERY.member.list, MemberListGetApi);
    // member detail fetch
    const [fetchMemberDetailData, memberDetailData] = useLazyQuery<MemberGetPayload>(KEYS.QUERY.member.detail, MemberDetailGetApi);
    // member invite list fetch
    const [fetchMemberInviteListData, memberInviteListData] = useLazyQuery<MemberInviteListPayload>(KEYS.QUERY.member.inviteList, MemberInviteListGetApi);
    // member invite info fetch
    const [fetchMemberInviteInfoData, memberInviteInfoData] = useLazyQuery<MemberInviteInfoPayload>(KEYS.QUERY.member.inviteInfo, MemberInviteInfoGetApi);
    // member invite info fetch
    const [fetchMemberShopInviteInfoData, memberInviteShopInfoData] = useLazyQuery<MemberInviteInfoPayload>(KEYS.QUERY.member.shopInviteInfo, MemberShopInviteInfoGetApi);
    // member invite info fetch
    const [fetchMemberRoleListData, memberRoleListData] = useLazyQuery<RoleListGetPayload>(KEYS.QUERY.member.roleList, RoleListGetApi);

    const [memberSignUpMutate] = useCustomMutation<MemberInfoPayload>(MemberSignUpPostApi); // member sign up
    const [memberEditMutate] = useCustomMutation<MemberPutPayload>(MemberPutApi);   // member edit
    const [memberDeleteMutate] = useCustomMutation<MemberDeletePayload>(MemberDeleteApi); // member delete
    const [memberTempPasswordMutate] = useCustomMutation<MemberStringPutPayload>(MemberTempPasswordPutApi);   // member send temp password
    const [memberChangePasswordMutate] = useCustomMutation<MemberPasswordPutPayload>(MemberChangePasswordPutApi);   // member change password
    const [memberResetPasswordMutate] = useCustomMutation<MemberResetPasswordPutPayload>(MemberResetPasswordPutApi);   // member change password
    const [memberInviteMutate, memberInviteResult, memberInviteStatus] = useCustomMutation<MemberPayload>(MemberInvitePostApi); // member invite
    const [memberInviteDeleteMutate] = useCustomMutation<MemberInviteInfoPayload>(MemberInviteDeleteApi); // member delete
    const [memberAddShopMutate] = useCustomMutation<MemberPayload>(MemberAddShopPostApi);   // member add shop
    const [memberAddRoleMutate] = useCustomMutation<RolePayload>(RolePostApi);  // member add role
    const [memberCertifyAccessMutate] = useCustomMutation<MemberAccessPutPayload>(MemberCertifyAccessPutApi);   // member certify access

    const _returnMutate = async () => {
        await queryClient.invalidateQueries({
            queryKey: KEYS.QUERY.member.list,
            refetchInactive: true
        })
    }

    // 로그인한 회원 정보 조회
    const getMemberMe = (callback?: (result: MemberMeEntity) => void) => {
        const params: MemberMePayload = {
            callback: (data) => {
                _callMemberMe(data, callback)
            }
        }
        fetchMemberMeData(params);
    }

    // 회원 정보를 global(recoil state)에 저장한다.
    const _callMemberMe = (data: ResponseEntity<MemberMeEntity>, callback?:(result: MemberMeEntity) => void) => {
        if(data && data.content) {
            let operation: _OPERATION_TYPE;
            let baseRole: _SERVER_ROLE_TYPE = 'MANAGER';
            let system: _SYSTEM_TYPE;

            //master 이면 -> selectedTopMenu:'op' 로 설정
            //brand 이면 -> selectedTopMenu: brandCode 로 설정
            //이외 -> selectedTopMenu: shopId로 설정
            let selectedTopMenu;
            let defaultSelectedTopMenu = 0;

            if(data.content.shops && data.content.shops.length > 0) {
                defaultSelectedTopMenu = data.content.shops![0].id ?? 0;
            }

            if(data.content.roles?.includes('MASTER')) {
                operation = 'OP';
                baseRole = 'MASTER';
                system = 'SPACE';
                selectedTopMenu = 'op';
            } else if(data.content.roles?.includes('SUPER_OWNER')) {
                operation = 'BRAND';
                baseRole = 'SUPER_OWNER';
                system = 'BRAND';
                selectedTopMenu = data.content.brandCode ?? defaultSelectedTopMenu;
            } else if(data.content.roles?.includes('SUPER')) {
                operation = 'BRAND'
                baseRole = 'SUPER';
                system = 'BRAND';
                selectedTopMenu = data.content.brandCode ?? defaultSelectedTopMenu;
            } else {
                operation = data.content.shops![0].shopType === 'CAFE' ? 'CAFE' : 'DINING';
                system = 'SHOP';
            }

            setSpaceInfoState({
                ...spaceInfoState,
                selectedShopId: defaultSelectedTopMenu,
                brandCode: data.content.brandCode!,
                brandId: data.content.brandId,
                selectedOperation: operation,
                selectedSystem: system,
                selectedTopMenu: selectedTopMenu ?? defaultSelectedTopMenu,
            });

            setMemberMeInfoState({
                ...data.content,
                baseRole: baseRole
            });

            callback && callback(data.content);
        }
    }

    // 회원 리스트 호출
    const getMemberList = ({brandCode, shopId, userId, name, page, sort, size}: MemberListGetPayload) => {
        const params: MemberListGetPayload = {
            page,
            size,
            sort,
            brandCode,
            shopId,
            userId,
            name
        }
        fetchMemberListData(params);

    }

    // 회원 정보
    const getMemberDetail = ({id}: MemberGetPayload) => {
        const params: MemberGetPayload = {
            id
        }
        fetchMemberDetailData(params);
    }

    // 회원 초대 리스트
    const getMemberInviteList = ({brandCode, shopId}: MemberInviteListPayload) => {
        const params: MemberInviteListPayload = {
            brandCode,
            shopId
        }
        fetchMemberInviteListData(params)
    }

    // 회원 초대 정보 (회원이 이미 초대됐는지 확인?)
    const getMemberInviteInfo = ({brandCode, shopId, mailCode, callback}: MemberInviteInfoPayload) => {
        const params: MemberInviteInfoPayload = {
            brandCode,
            shopId,
            mailCode,
            callback: (data: ResponseEntity<MemberInviteInfoEntity>) => callback && callback(data)
        }
        fetchMemberInviteInfoData(params)
    }

    // 회원 추가 샵 초대 정보 (회원이 이미 초대됐는지 확인?)
    const getMemberShopInviteInfo = ({brandCode, shopId, mailCode, callback}: MemberInviteInfoPayload) => {
        const params: MemberInviteInfoPayload = {
            brandCode,
            shopId,
            mailCode,
            callback: (data: ResponseEntity<MemberInviteInfoEntity>) => callback && callback(data)
        }
        fetchMemberShopInviteInfoData(params)
    }

    // 권한 리스트 호출
    const getMemberRoleList = () => {
        const params: RoleListGetPayload = {}
        fetchMemberRoleListData(params)
    }

    // 회원 가입
    const onMemberSignUp = ({email, name, userPwd, brandCode, shopId, roleId, inviteAt, mailCode, companyIds}: MemberInfoPayload, callback?: (...args: any[]) => void) => {
        const params: MemberInfoPayload = {
            email, name, userPwd, brandCode, shopId, roleId, inviteAt, mailCode, companyIds,
            callback: (data: ResponseEntity<IdEntity>, params?: MemberInfoPayload, errorResponse?: any) => {
                if(callback){
                    if(data.content?.id){
                        callback(data);
                    }else{
                        callback(undefined, undefined, errorResponse);
                    }
                }
            }
        }
        memberSignUpMutate(params)
    }

    // 회원 수정.
    const onMemberEdit = ({id, name, fileAttachId, companyIds, memo}: MemberPutPayload, callback?: (result: ResponseEntity<boolean>) => void) => {
        const params: MemberPutPayload = {
            id, name, fileAttachId, memo, companyIds,
            callback: (result: ResponseEntity<boolean>) => {
                _returnMutate()
                if(id === memberMeInfoState.id) {
                    // 내 계정이면 정보를 다시 불러온다.
                    getMemberMe();
                }
                callback && callback(result)
            }
        }
        memberEditMutate(params)
    }

    // 내정보를 수정.
    // 내 정보 수정 후 memberMe 호춣하여 recoil을 변경한다.
    const onMyEdit = ({id, fileAttachId}: MemberPutPayload, callback?: (result: ResponseEntity<string>) => void) => {
        const params: MemberPutPayload = {
            id,
            fileAttachId,
            callback: (result: ResponseEntity<string>) => {
                getMemberMe()
                callback && callback(result)
            }
        }
        memberEditMutate(params)
    }

    // 회원을 삭제한다.
    const onMemberDelete = (data: MemberDeletePayload, callback?: (result: ResponseEntity<boolean>) => void) => {
        const params: MemberDeletePayload = {
            ...data,
            callback: (result: ResponseEntity<boolean>) => {
                _returnMutate();
                callback && callback(result);
            }
        }

        memberDeleteMutate(params)
    }

    // 회원 임시 비번
    const onMemberTempPassword = ({data}: MemberStringPutPayload) => {
        const params: MemberStringPutPayload = {
            data
        }
        memberTempPasswordMutate(params)
    }

    // 회원 비번 변경
    const onMemberChangePassword = ({oldPassword, newPassword, callback}: MemberPasswordPutPayload) => {
        const params: MemberPasswordPutPayload = {
            oldPassword,
            newPassword,
            callback: (res: ResponseEntity<boolean>) => {
                console.log(res)
                callback && callback(res);
            }
        }
        memberChangePasswordMutate(params)
    }

    // 회원 비번 초기화
    const onMemberResetPassword = ({brandCode, userId, userPwd, callback}: MemberResetPasswordPutPayload) => {
        const params: MemberResetPasswordPutPayload = {
            brandCode,
            userId,
            userPwd,
            callback: (res: ResponseEntity<boolean>) => {
                callback && callback(res);
            }
        }
        memberResetPasswordMutate(params)
    }

    // 회원 초대 등록
    const onMemberInvite = ({brandCode, email, roleId, shopId}: MemberPayload, callback?: (data) => void) => {
        const params: MemberPayload = {
            brandCode,
            email,
            roleId,
            shopId,
            callback: (data) => {
                queryClient.invalidateQueries(KEYS.QUERY.member.inviteList, {refetchInactive: true})
                callback && callback(data);
            }
        }
        memberInviteMutate(params);
    }

    // 회원 초대 삭제
    const onMemberInviteDelete = ({brandCode, mailCode, shopId}: MemberInviteInfoPayload) => {
        const params: MemberInviteInfoPayload = {
            brandCode,
            mailCode,
            shopId,
            callback: () => {
                queryClient.invalidateQueries(KEYS.QUERY.member.inviteList, {refetchInactive: true})
            }
        }
        memberInviteDeleteMutate(params)
    }

    // 멤버에게 샵을 추가한다.
    const onMemberAddShop = ({...args}: MemberPayload) => {
        const params: MemberPayload = {
            ...args
        }
        memberAddShopMutate(params);
    }

    // 멤버에게 권한을 추가한다.
    const onRoleAdd = ({id, name, status}: RolePayload) => {
        const params: RolePayload = {
            id,
            name,
            status
        }
        memberAddRoleMutate(params);
    }

    const callMemberMeInMyMenu = (data: ResponseEntity<MemberMeEntity>) => {
        _callMemberMe(data);
    }

    // 개인정보 접근 인증
    const onMemberCertifyAccess = ({userPwd, reason,accessBrandCode, deviceInfo, callback}: MemberAccessPutPayload) => {
        const params: MemberAccessPutPayload = {
            userPwd,
            reason,
            accessBrandCode,
            deviceInfo,
            callback: (res: ResponseEntity<boolean>) => {
                callback && callback(res);
            }
        }
        memberCertifyAccessMutate(params)
    }

    return {
        memberMeData,
        memberListData,
        memberDetailData,
        memberMeInfoState,
        memberInviteListData,
        memberInviteInfoData,
        memberInviteShopInfoData,
        memberRoleListData,
        getMemberMe,
        getMemberList,
        getMemberDetail,
        getMemberInviteList,
        getMemberInviteInfo,
        getMemberShopInviteInfo,
        getMemberRoleList,
        onMemberSignUp,
        onMemberEdit,
        onMemberDelete,
        onMemberTempPassword,
        onMemberChangePassword,
        onMemberResetPassword,
        onMemberInvite,
        onMemberInviteDelete,
        onMemberAddShop,
        onRoleAdd,
        onMyEdit,
        onMemberCertifyAccess,
        memberInviteResult,
        memberInviteStatus,
        callMemberMeInMyMenu
    }
}

export default MemberRepository;